import { generateDailyPuzzle, generateWelcomePuzzle, modeForDate } from '../../engine/generate';
import { fmtTime, t } from '../../i18n/runtime';
import { celebrateHaptic, successChord } from '../celebrate';
import { canRedo, dailyRedoKey, profile, recordSolve, setSolvePct } from '../../state/profile';
import { fetchDaily, postSolve } from '../../state/sync';
/**
 * G2 — Daily Challenge play (v2.2 — no credit gate, no help pill).
 * Per UI-SPEC §3 G2 + GAME-SPEC §1.1.
 *
 * Chrome fades on first valid drag; timer + rarity + reset visible at rest.
 * v2.2: every player can attempt today's Daily without spending credits;
 * Practice mode unlocks for 24h after Daily is solved (or always for Pro).
 */
import { el, html, on } from '../html';
import { iconString } from '../icons';
import { type PlayController, mountPlay } from '../play-engine';
import { openRulesSheet } from '../rules-sheet';
import type { RouteContext } from '../router';

function modeForToday(dateStr: string): string {
  return modeForDate(new Date(dateStr + 'T00:00:00Z'));
}

/**
 * Inline SVG rarity glyphs — replaces the Unicode character map.
 * Unicode rarity chars (●◆▲★✦) render inconsistently across iOS font
 * fallback chains; some users reported the ◆ looking like a letter "U"
 * on Build 11/12 testflight. SVG paths are pixel-identical everywhere.
 *
 * Each glyph is sized to fit a 14×14 box with currentColor fill.
 */
function rarityGlyphSvg(rarity: string): string {
  const paths: Record<string, string> = {
    common:    `<circle cx="8" cy="8" r="5" fill="currentColor"/>`,
    uncommon:  `<path d="M8 2 L14 8 L8 14 L2 8 Z" fill="currentColor"/>`,
    rare:      `<path d="M8 2 L14 13 L2 13 Z" fill="currentColor"/>`,
    epic:      `<path d="M8 2 L9.8 6.2 L14.4 6.5 L10.8 9.5 L12 14 L8 11.5 L4 14 L5.2 9.5 L1.6 6.5 L6.2 6.2 Z" fill="currentColor"/>`,
    legendary: `<path d="M8 1.5 L9.4 5.8 L13.8 5 L11.2 8.5 L13.8 12 L9.4 11.2 L8 15.5 L6.6 11.2 L2.2 12 L4.8 8.5 L2.2 5 L6.6 5.8 Z" fill="currentColor"/>`,
  };
  return `<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">${paths[rarity] ?? paths['common']}</svg>`;
}

const RARITY_LABEL_KEY: Record<string, string> = {
  common: 'rarity.common',
  uncommon: 'rarity.uncommon',
  rare: 'rarity.rare',
  epic: 'rarity.epic',
  legendary: 'rarity.legendary',
};

export default function (
  ctx: RouteContext,
): HTMLElement | { node: HTMLElement; cleanup: () => void } {
  const dateParam = ctx.params['date'] ?? '';
  // FDT-O-001 — '/play/welcome' serves the fixed first-run warm-up (a guaranteed
  // 4-vertex puzzle that teaches the rule by doing) instead of a daily puzzle.
  const isWelcome = dateParam === 'welcome';

  // Replay budget gate: an already-solved daily may be replayed to improve the
  // time up to REDO_LIMIT times. Once spent, /play/:date (e.g. a deep link or
  // the redo button) bounces to the read-only review instead of letting the
  // player keep grinding the leaderboard.
  const alreadySolved = !isWelcome && profile().get().history.some((h) => h.date === dateParam);
  if (alreadySolved && !canRedo(dailyRedoKey(dateParam))) {
    queueMicrotask(() => ctx.navigate(`/review/${dateParam}`));
    return el(html`<section class="screen screen-play" aria-hidden="true"></section>`);
  }

  const puzzle = isWelcome ? generateWelcomePuzzle() : generateDailyPuzzle(dateParam);

  // v2.2: no credit gate on Daily — every player has unlimited attempts
  // to today's puzzle. Practice mode handles its own 24h gate.

  let controller: PlayController | null = null;
  let timerHandle: ReturnType<typeof setInterval> | null = null;
  let chromeTimer: ReturnType<typeof setTimeout> | null = null;
  let startedAt = Date.now(); // FDT-O-007: re-settable so Reset restarts the clock

  /*
   * SCORING + TIMER MECHANICS — Daily mode (this screen)
   * ─────────────────────────────────────────────────────
   * Score: time-to-solve (lower = better). Rank computed server-side
   *        via Elo update relative to today's solver pool.
   * Timer: starts at first pointerdown, ticks until onWin. Untimed —
   *        there is NO hard expiry on Daily. The number is purely
   *        informational and feeds into the leaderboard time_ms.
   * Time-pressure cue: subtle ink-color tint deepens at 60s / 120s /
   *        300s to nudge attention without imposing failure.
   * Attempts: each "Reset path" press increments attempts; reflected
   *        in the solve record so the leaderboard can distinguish a
   *        clean first-attempt solve from a multi-restart one.
   */
  const root = el(html`
    <section class="screen screen-play" data-chrome="full" data-rarity="${puzzle.rarity}" aria-label="${isWelcome ? t('play.welcome_label') : t('home.today_label')}">
      <header class="play-topbar" data-topbar>
        <div class="topbar-left">
          <button class="topbar-btn" aria-label="${t('common.close')}" data-exit>${iconString('close', 20)}</button>
          <button class="topbar-btn" aria-label="${t('rules.title')}" data-rules>${iconString('help', 20)}</button>
        </div>
        <span class="topbar-rarity-chip rarity-${puzzle.rarity}" role="status"
              aria-label="${t('game.difficulty_label')}: ${t(RARITY_LABEL_KEY[puzzle.rarity] ?? 'rarity.common')}">
          <span class="topbar-rarity-glyph" aria-hidden="true">${rarityGlyphSvg(puzzle.rarity)}</span>
          <span class="topbar-rarity-text">${t(RARITY_LABEL_KEY[puzzle.rarity] ?? 'rarity.common')}</span>
        </span>
        <span class="topbar-timer" data-pressure="rest" aria-label="${t('game.timer_label')}" aria-live="off">
          <span class="icon" aria-hidden="true">⏱</span><span class="time" data-timer>0:00</span>
        </span>
      </header>
      <div class="play-canvas-wrap" data-canvas></div>
      <footer class="play-footer">
        <button class="play-footer-btn" data-undo>${t('game.undo')}</button>
        <span class="progress" aria-live="polite">
          <span data-done>0</span> / <span data-total>${puzzle.edges.length}</span> ${t('game.edges')}
        </span>
        <button class="play-footer-btn" data-reset>${t('game.reset_path')}</button>
      </footer>
    </section>
  `);

  const canvas = root.querySelector('[data-canvas]') as HTMLElement;
  const doneEl = root.querySelector('[data-done]') as HTMLElement;
  const timerEl = root.querySelector('[data-timer]') as HTMLElement;
  const timerHost = root.querySelector('.topbar-timer') as HTMLElement;
  const reduceMotion =
    profile().get().a11y.reduceMotion === 'on' ||
    (profile().get().a11y.reduceMotion === 'auto' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches);

  /**
   * Audio feedback — Web Audio API synthesis (no asset files per UI-2).
   * Three discrete tones: connect (short blip), reset (low blip),
   * complete (two-note triumphant). Driven by profile.soundEnabled
   * (Settings → Sound toggle).
   */
  const soundEnabled = profile().get().soundEnabled !== false;
  let audioCtx: AudioContext | null = null;
  function tone(freq: number, durMs: number, type: OscillatorType = 'sine', vol = 0.06): void {
    if (!soundEnabled) return;
    try {
      audioCtx = audioCtx ?? new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const ctx = audioCtx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durMs / 1000);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + durMs / 1000);
    } catch { /* audio init can fail before first gesture — silently ignore */ }
  }

  const pf = profile().get();
  // Welcome always shows hints — the start-glow IS the teaching moment. (I-19
  // lets experienced players turn hints off later, but a first-timer must see
  // them; the welcome bubble is independent of the daily's once-ever bubble.)
  const hintsOn = isWelcome ? true : pf.hintsEnabled !== false;
  controller = mountPlay(canvas, {
    puzzle,
    reduceMotion,
    hints: hintsOn,
    firstHintBubble: isWelcome ? true : hintsOn && !pf.hintBubbleShown,
    onHintBubbleShown: () => {
      if (!isWelcome) profile().set({ hintBubbleShown: true });
    },
    hintLabels: { startBubble: t('play.hint.startGlow'), stuck: t('play.hint.stuck') , noRepeat: t('play.hint.noRepeat') },
    onProgress: (done) => {
      doneEl.textContent = String(done);
      celebrateHaptic('light'); // moment 1 — connect (gated by Settings haptic toggle)
      // Sound: connection blip (A5 = 880Hz, 80ms) — short + non-intrusive
      tone(880, 80, 'sine', 0.05);
    },
    onWin: async ({ timeMs, attempts: solveAttempts, path }) => {
      if (isWelcome) {
        // First win ever — celebrate (two-note + success haptic), mark the
        // welcome gate done, then hand off to G1 home which presents
        // "Today's Fudeto" (the "這是今天的題目 →" beat). Welcome never records a
        // solve and never claims to be the daily (R-001a).
        celebrateHaptic('success'); // moment 2 — complete
        successChord(soundEnabled); // synthesized C-E-G (no asset file)
        profile().set({ welcomeShown: true });
        if (timerHandle) clearInterval(timerHandle);
        setTimeout(() => ctx.navigate('/'), 1100); // let the path-replay admire beat play
        return;
      }
      const dur = Math.round(timeMs / 1000);
      const record = {
        date: dateParam,
        timeMs: dur * 1000,
        attempts: solveAttempts,
        rarity: puzzle.rarity,
        mode: modeForToday(dateParam),
        path, // the player's actual trail → Review can replay "my path"
      };
      recordSolve(record);
      celebrateHaptic('success'); // moment 2 — complete
      successChord(soundEnabled); // synthesized C-E-G major triad (no asset file)
      if (timerHandle) clearInterval(timerHandle);
      // FDT-O-010 — within the path-replay window (FDT-O-005), fetch standing:
      // store the new rating (drives tier) + compute THIS puzzle's speed
      // percentile for the solve screen. Bounded so a slow/offline worker never
      // delays the transition (postSolve queues the solve regardless).
      let pct: number | null = null;
      try {
        pct = await Promise.race([
          (async (): Promise<number | null> => {
            const [s, daily] = await Promise.all([
              postSolve(record, puzzle.edges.length),
              fetchDaily(dateParam),
            ]);
            if (s.rating != null) profile().set({ rating: s.rating });
            return s.rank && daily?.solver_count
              ? Math.max(0, Math.min(1, (daily.solver_count - s.rank) / daily.solver_count))
              : null;
          })(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 1300)),
        ]);
      } catch {
        /* offline — postSolve already queued the solve */
      }
      // FDT-O-013 — persist this solve's speed percentile so the Stats page
      // can show "比 X% 玩家更快" for today without a re-fetch.
      if (pct != null) setSolvePct(record.date, pct);
      const pctQ = pct != null ? `&pct=${pct.toFixed(4)}` : '';
      ctx.navigate(`/solve/${dateParam}?t=${timeMs}&a=${solveAttempts}${pctQ}`);
    },
  });

  // Timer tick + gradual time-pressure cue.
  // Daily mode has NO hard expiry — these tiers are purely visual nudges:
  //   rest    (0–60s)   — neutral ink-soft color
  //   warm    (60–120s) — full ink color
  //   focus   (120s+)   — ink + subtle gold tint (var(--color-gold))
  timerHandle = setInterval(() => {
    const elapsedSec = (Date.now() - startedAt) / 1000;
    timerEl.textContent = fmtTime(elapsedSec);
    const tier = elapsedSec < 60 ? 'rest' : elapsedSec < 120 ? 'warm' : 'focus';
    if (timerHost.dataset['pressure'] !== tier) timerHost.dataset['pressure'] = tier;
  }, 250);

  // Chrome fade ladder.
  chromeTimer = setTimeout(() => root.setAttribute('data-chrome', 'faded'), 1500);
  canvas.addEventListener('pointerdown', () => {
    if (chromeTimer) clearTimeout(chromeTimer);
    root.setAttribute('data-chrome', 'full');
    chromeTimer = setTimeout(() => root.setAttribute('data-chrome', 'faded'), 1500);
  });

  on(root, 'click', '[data-exit]', () => {
    if (isWelcome) {
      // Skipping the warm-up is allowed and shouldn't nag — close the gate.
      profile().set({ welcomeShown: true });
      ctx.navigate('/');
      return;
    }
    if (confirm(t('game.confirm_exit'))) ctx.navigate('/');
  });
  // Optional rule reference (I-14/I-19 safe): reuses the existing how-to-play +
  // scoring sheet, on demand only — no forced tutorial text on the board.
  on(root, 'click', '[data-rules]', () => openRulesSheet(root));
  on(root, 'click', '[data-undo]', () => controller?.undo());
  on(root, 'click', '[data-reset]', () => {
    controller?.reset();
    // FDT-O-007 — Reset restarts the elapsed clock from zero (this attempt's time).
    startedAt = Date.now();
    timerEl.textContent = fmtTime(0);
    timerHost.dataset['pressure'] = 'rest';
    // Soft low blip on reset — distinct from connection sound
    tone(220, 100, 'sine', 0.04);
    celebrateHaptic('light');
  });

  return {
    node: root,
    cleanup: () => {
      if (timerHandle) clearInterval(timerHandle);
      if (chromeTimer) clearTimeout(chromeTimer);
      controller?.destroy();
    },
  };
}
